import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Logger, LoggerFactory } from '../../../app/core/logger';
import { AppStorage } from '../../../app/core/services/app-storage.service';
import { SettingsState } from './settings.reducer';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { SettingsActions } from './settings.actions';
import { LanguageService } from '../../../libs/system';
import { AuthActions } from '@rillion/store/auth/auth.actions';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SettingsEffects {
  private readonly logger: Logger;

  constructor(
    logService: LoggerFactory,
    @Inject(DOCUMENT) private document: Document,
    private readonly appStorage: AppStorage,
    private readonly router: Router,
    private readonly store: Store<SettingsState>,
    private readonly translateService: TranslateService,
    private readonly actions$: Actions,
    private readonly languageService: LanguageService,
  ) {
    this.logger = logService.getLogger('SettingsEffects');
  }

  loadLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(({ user: { languageCode } }) => {
        this.translateService.use(languageCode);
        return SettingsActions.loadLanguage({ languageCode });
      }),
    ),
  );

  setPageSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserSuccess),
      map(({ user }) => user.rows),
      filter((rows) => !!rows),
      map((rows) => SettingsActions.setPageSize({ pageSize: Number(rows) })),
    ),
  );

  loadSettings = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.loadSettings),
        tap(() => this.logger.info('Loading settings...')),
        tap(() =>
          this.appStorage.loadData('MINI', false).pipe(
            first(),
            tap((mini) => {
              if (mini === undefined) {
                mini = false;
              }
              this.store.dispatch(SettingsActions.miniSideNavFlag({ mini }));
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadLanguage = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadLanguage),
      tap(() => this.logger.info('Loading Language...')),
      switchMap(({ languageCode }) => this.languageService.apiV10LanguageLanguageIDGet(languageCode)),
      map(({ data }) => SettingsActions.setLanguage({ language: data[0] })),
    ),
  );

  loadMiniSideNav = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.loadMiniSideNav),
        tap(() => this.logger.info('Loading mini sidenav position ...')),
        switchMap(() =>
          this.appStorage.loadData('MINI', false).pipe(
            first(),
            map((mini) => {
              if (mini === undefined) {
                mini = false;
              }
              return SettingsActions.miniSideNavFlag({ mini });
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadTheme = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadTheme),
      tap(() => this.logger.info('Loading theme...')),
      switchMap(({ themes }) =>
        this.appStorage.loadData('THEME', '').pipe(
          first(),
          map((theme) => {
            if (theme === undefined || !themes.includes(theme)) {
              theme = themes[0];
            }
            return SettingsActions.applyTheme({ theme });
          }),
        ),
      ),
    ),
  );

  updateTheme = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.applyTheme),
        tap(({ theme }) => {
          this.appStorage.saveData('THEME', theme);
          this.applyTheme(theme);
        }),
      ),
    { dispatch: false },
  );

  updateMiniSideNavStatus = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.miniSideNavFlag),
        tap(({ mini }) => {
          this.appStorage.saveData('MINI', mini);
          this.logger.debug(`Saved SideNav as ${mini ? 'Mini' : 'Expanded'}`);
        }),
      ),
    { dispatch: false },
  );

  /**
   * Applies a theme to the App.
   *
   * @param theme The theme name or default will be used
   */
  private applyTheme(theme = 'rillion-light-theme') {
    const classList = this.document.documentElement.classList;
    const toRemove = Array.from(classList).filter((item: string) => item.includes('-theme'));
    if (toRemove.length) {
      classList.remove(...toRemove);
    }
    classList.add(theme);

    this.logger.debug(`Applied ${theme}`);
  }
}
